import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Typography } from '@mui/material';
import type { Asset } from 'types/asset.types';
import { InteractiveModal } from 'components/Modals/InteractiveModal/BaseModal';
import AssetCardItem from '../AssetCardItem/index';
import FooterDisplay from './FooterDisplay';
import { ThemePalette } from 'mui.theme';
import ComparerModalFilter from './ModalSidebar';
import { sortCompareAssetsByChecked } from '../utils';
import useAppTheme from 'hooks/useAppTheme';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  title: string;
  open: boolean;
  handleAbort: () => void;
  selectedAsset: string[];
  filteredAssets: Asset[];
}

export const CompareAssetModal = ({
  title,
  handleAbort,
  open,
  selectedAsset,
  filteredAssets,
}: Props) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const navigate = useNavigate();
  const [assetsDisplay, setAssetsDisplay] = useState<Asset[]>(filteredAssets);
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([
    ...selectedAsset,
  ]);

  const handleSubmit = () => {
    handleAbort();
    const selectedAssets = filteredAssets.filter((item) =>
      selectedAssetIds.includes(item.assetId)
    );
    navigate('/compare-assets', { state: { assets: selectedAssets } });
  };

  function handleCheckBoxSelected(arg: string) {
    let result: string[] = selectedAssetIds;

    const itemExists = selectedAssetIds.find((item) => item === arg);
    if (itemExists) {
      result = selectedAssetIds.filter((item) => item !== arg);
    } else {
      // Handle limit to 3
      if (selectedAssetIds.length > 2) {
        return;
      }
      result = [...selectedAssetIds, arg];
    }
    setSelectedAssetIds(result);
  }

  const defaultSelectedAssetType =
    filteredAssets.find((item) => item.assetId === selectedAsset[0])
      ?.assetType ?? '';

  return (
    <InteractiveModal
      initialState={open}
      title={title}
      handleAbort={handleAbort}
      SuccessButtonProps={{
        onClick: () => {
          handleSubmit();
        },
        disabled: selectedAssetIds.length < 2,
        label: t('compare'),
      }}
      ModalContentProps={{
        gridItemProps: {
          lg: 12,
        },
      }}
      cancelButtonText={t('cancel')}
      confirmCancellation={selectedAssetIds.length > 1}
      enableDarkTheme={enableDarkTheme}
      footerMargin={'2px'}
    >
      <div style={{ maxHeight: '65vh', overflowY: 'auto', width: '100%' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
            maxHeight: '45vh',
            overflowY: 'hidden',
            gap: '10px',
            width: '100%',
          }}
        >
          {/* filter section */}
          <div
            style={{
              height: '45vh',
              overflowY: 'auto',
              gridColumn: 'span 2',
            }}
          >
            <Typography
              sx={{
                color: palette.textPrimary,
                fontSize: '14px',
              }}
            >
              {t('you_can_select_up_to_3_assets_to_compare')}
            </Typography>
            <Typography
              sx={{
                color: palette.textPrimary,
                fontSize: '23px',
                paddingTop: '10px',
              }}
            >
              {t('compare_assets_filters')}
            </Typography>
            <ComparerModalFilter
              enableDarkTheme={enableDarkTheme}
              assets={filteredAssets}
              setAssetsDisplay={setAssetsDisplay}
              defaultAssetType={defaultSelectedAssetType}
              setSelectedAssetIds={setSelectedAssetIds}
            />
          </div>
          {/* asset selection  */}
          <div
            style={{
              height: '45vh',
              overflowY: 'auto',
              gridColumn: 'span 4',
            }}
          >
            <div
              id="assetCards"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                maxHeight: '45vh',
                overflowY: 'auto',
                gap: '10px',
                width: '100%',
              }}
            >
              {/* Main content grid */}
              {assetsDisplay.length > 0 ? (
                sortCompareAssetsByChecked(selectedAssetIds, assetsDisplay).map(
                  (asset: Asset) => {
                    return (
                      <AssetCardItem
                        key={asset.assetId}
                        asset={asset}
                        checkedList={selectedAssetIds}
                        showCheckedButton={true}
                        handleCheckBoxSelected={handleCheckBoxSelected}
                      />
                    );
                  }
                )
              ) : (
                <Typography
                  sx={{
                    color: palette.textPrimary,
                  }}
                >
                  {t('no_assets_available_2')}
                </Typography>
              )}
            </div>
          </div>
        </div>
        {selectedAssetIds.length > 0 ? (
          <FooterDisplay
            assets={filteredAssets}
            selectedAssetIds={selectedAssetIds}
            enableDarkTheme={enableDarkTheme}
            handleCheckBoxSelected={handleCheckBoxSelected}
          />
        ) : (
          <></>
        )}
      </div>
      <></>
    </InteractiveModal>
  );
};
